import PropTypes from "prop-types";
import LeaderboardOutcomeSummary from "../../../pages/campaigns/setupPages/SummaryPage/LeaderboardOutcomeSummary";
import { isEmptyObject, updateSweepstakesOutcomes } from "../../../utils/commons";
import { isOutcomeRuleConfigured } from "../campaignUtils";
import DirectRewardsRedemptionOutcome from "../rulesAndOutcomes/outcomes/directRewardsRedemption/DirectRewardsRedemptionOutcome";
import { RetailerConditionDisplay } from "../rulesAndOutcomes/outcomes/retailerOutcome";
import "../rulesAndOutcomes/outcomes/retailerOutcome/retailerOutcome.scss";
import { StoreConditionDisplay } from "../rulesAndOutcomes/outcomes/storeOutcome";
import "../rulesAndOutcomes/outcomes/storeOutcome/storeOutcome.scss";
import { SurveyConditionDisplay } from "../rulesAndOutcomes/outcomes/surveyOutcome";
import "../rulesAndOutcomes/outcomes/surveyOutcome/surveyOutcome.scss";
import StreakConfigView from "../viewComponents/StreakConfigView";
import ViewComponentsRenderer from "../viewComponents/ViewComponentsRenderer";
import "./activitySetup.scss";

const SURVEY_RULE_SLUG = "survey-answer-matching-rule";
const RETAILER_RULE_SLUG = "retailer-matching-rule";
const STORE_GROUP_RULE_SLUG = "store-group-matching-rule";

const renderOutcomeContent = (outcome) => {
  const slug = outcome?.outcomeDefinition?.slug;

  if (slug === "award-leaderboard-points") {
    return (
      <div className="additional-fields-container">
        <LeaderboardOutcomeSummary
          outcome={outcome}
          key={outcome?.outcomeData?.leaderboard_id}
        />
      </div>
    );
  }

  if (slug === "points-based-streaks") {
    return (
      <StreakConfigView
        data={outcome?.outcomeData}
        key={outcome?.outcomeData?.streak_config_id || outcome?.id}
      />
    );
  }

  if (
    slug === "direct-redemption-rewards" ||
    slug === "direct-redemption-rewards-b2b"
  ) {
    return <DirectRewardsRedemptionOutcome outcome={outcome} />;
  }

  if (
    slug &&
    !isEmptyObject(outcome?.outcomeDefinition?.inputSchema) &&
    !isEmptyObject(outcome?.outcomeDefinition?.uiSchema)
  ) {
    return (
      <div className="ml-3">
        <ViewComponentsRenderer
          customComponentName={slug}
          data={outcome?.outcomeData}
          inputSchema={outcome?.outcomeDefinition?.inputSchema}
          uiSchema={outcome?.outcomeDefinition?.uiSchema}
          definition={outcome}
          hiddenFields={["keywords"]}
        />
      </div>
    );
  }

  return null;
};

export const ActivityOutcomesSummary = ({ data, listSweepstakes }) => {
  // Add the outcome name to outcomesData
  const updatedOutcomesData = updateSweepstakesOutcomes(data, listSweepstakes);

  return (
    <div className="display-flex flex-direction-column gap-2">
      {updatedOutcomesData?.map((outcome) => {
        const rules = outcome?.outcomeRules || [];
        const surveyRule = rules.find(
          (r) => r?.ruleDefinition?.slug === SURVEY_RULE_SLUG
        );
        const retailerRule = rules.find(
          (r) => r?.ruleDefinition?.slug === RETAILER_RULE_SLUG
        );
        const storeRule = rules.find(
          (r) => r?.ruleDefinition?.slug === STORE_GROUP_RULE_SLUG
        );
        const hasSurveyCondition = !!surveyRule?.ruleData?.condition_display;
        const hasRetailerCondition =
          Array.isArray(retailerRule?.ruleData?.keywords) &&
          retailerRule.ruleData.keywords.length > 0;
        const hasStoreCondition =
          !!storeRule?.ruleData?.store_configuration &&
          Array.isArray(storeRule?.ruleData?.keywords) &&
          storeRule.ruleData.keywords.length > 0;

        return (
          <div className="outcome-card" key={outcome.id}>
            {/* Outcome title */}
            <div className="outcome-title-container">
              <h3 className="campaign-name fs-14">
                {outcome?.outcomeDefinition?.name ?? outcome.name}
              </h3>
            </div>

            {/* Outcome data */}
            {renderOutcomeContent(outcome)}

            {/* Outcome rules (excluding survey/retailer/store rules rendered separately) */}
            {outcome?.outcomeRules
              ?.filter(isOutcomeRuleConfigured)
              .filter(
                (rule) =>
                  rule?.ruleDefinition?.slug !== SURVEY_RULE_SLUG &&
                  rule?.ruleDefinition?.slug !== RETAILER_RULE_SLUG &&
                  rule?.ruleDefinition?.slug !== STORE_GROUP_RULE_SLUG
              )
              .map((rule, index) => (
                <div className="ml-3" key={rule.id || `rule-${index}`}>
                  {!isEmptyObject(rule?.ruleData) && (
                    <ViewComponentsRenderer
                      customComponentName={rule.ruleDefinition?.slug}
                      data={rule.ruleData}
                      inputSchema={rule.ruleDefinition?.inputSchema}
                      uiSchema={rule.ruleDefinition?.uiSchema}
                      ruleDefinition={rule.ruleDefinition}
                      hiddenFields={["keywords"]}
                    />
                  )}
                </div>
              ))}

            {/* Survey condition display */}
            {hasSurveyCondition && (
              <SurveyConditionDisplay ruleData={surveyRule.ruleData} readOnly />
            )}

            {/* Retailer condition display */}
            {hasRetailerCondition && (
              <RetailerConditionDisplay ruleData={retailerRule.ruleData} readOnly />
            )}

            {/* Store condition display */}
            {hasStoreCondition && (
              <StoreConditionDisplay ruleData={storeRule.ruleData} readOnly />
            )}
          </div>
        );
      })}
    </div>
  );
};

ActivityOutcomesSummary.propTypes = {
  data: PropTypes.array,
  listSweepstakes: PropTypes.array,
};
